import * as R from 'ramda'
import { moment } from '@pbt/pbt-ui-components'

import type { OrderVaccinationDetails } from '~/types'

import type {
  InventoryDetails,
  PatientDetails,
  VaccineDetailRow,
  VaccineDetailsTopRow,
} from './ChirpChargesVaccineDetails'

const additionalVaccineFields = [
  'manufacturedBy',
  'serialNumber',
  'location',
  'amount',
  'lotNumber',
  'lotExpiration',
  'dueDate',
]

export const hasAdditionalVaccineFields = (
  vaccinationDetails?: OrderVaccinationDetails,
) => R.any(Boolean, R.props(additionalVaccineFields, vaccinationDetails || {}))

export const getInitialChirpRows = (
  vaccinationDetails?: OrderVaccinationDetails,
): VaccineDetailRow[] => [
  {
    doseType: vaccinationDetails?.doseTypeId ?? '',
    expires: vaccinationDetails?.lotExpiration ?? '',
    vaccineType:
      vaccinationDetails?.typeId ?? vaccinationDetails?.vaccineTypeId ?? '',
  },
]

export const getInitialInventoryDetails = (
  vaccinationDetails?: OrderVaccinationDetails,
): InventoryDetails => ({
  dateGiven: vaccinationDetails?.dateGiven ?? '',
  lotNumber: vaccinationDetails?.lotNumber ?? '',
  manufacturedBy: vaccinationDetails?.manufacturedBy ?? '',
  name: (vaccinationDetails as { name?: string } | undefined)?.name ?? '',
  serialNumber: vaccinationDetails?.serialNumber ?? '',
})

export const getInitialVaccineDetailsTopRow = (
  vaccinationDetails?: OrderVaccinationDetails,
  soapDoctorId?: string,
): VaccineDetailsTopRow => ({
  assignedVeterinarian:
    vaccinationDetails?.administeredBy ?? soapDoctorId ?? '',
  dateGiven: vaccinationDetails?.dateGiven ?? '',
  deliveryMethod: vaccinationDetails?.deliveryMethodId ?? '',
})

export const getInitialPatientDetails = (
  vaccinationDetails?: OrderVaccinationDetails,
): PatientDetails => ({
  animalControlLicense: vaccinationDetails?.animalControlLicense ?? '',
  color: vaccinationDetails?.color ?? '',
  license: vaccinationDetails?.license ?? '',
  markingsTattoo: vaccinationDetails?.markingsTattoo ?? '',
  microchip: vaccinationDetails?.microchip ?? '',
  rabiesTagNumber: vaccinationDetails?.rabiesTag ?? '',
})

const normalizeDateToIso = (value?: string) => {
  if (!value) {
    return undefined
  }

  const parsed = moment(
    value,
    ['MM/DD/YYYY', 'MM/DD/YY', moment.ISO_8601],
    true,
  )

  if (!parsed.isValid()) {
    return value
  }

  return parsed.startOf('day').toISOString()
}

export const mapChirpStateToVaccinationDetails = ({
  currentVaccinationDetails,
  chirpInventoryDetails,
  chirpPatientDetails,
  chirpRows,
  chirpVaccineDetailsTopRow,
}: {
  chirpInventoryDetails: InventoryDetails
  chirpPatientDetails: PatientDetails
  chirpRows: VaccineDetailRow[]
  chirpVaccineDetailsTopRow: VaccineDetailsTopRow
  currentVaccinationDetails?: OrderVaccinationDetails
}): OrderVaccinationDetails => {
  const firstRow = chirpRows[0]
  const vaccineTypeId = firstRow?.vaccineType || undefined

  return {
    ...currentVaccinationDetails,
    administeredBy: chirpVaccineDetailsTopRow.assignedVeterinarian || undefined,
    dateGiven: normalizeDateToIso(
      chirpVaccineDetailsTopRow.dateGiven || chirpInventoryDetails.dateGiven,
    ),
    deliveryMethodId: chirpVaccineDetailsTopRow.deliveryMethod || undefined,
    doseTypeId: firstRow?.doseType || undefined,
    lotExpiration: normalizeDateToIso(firstRow?.expires),
    lotNumber: chirpInventoryDetails.lotNumber || undefined,
    microchip: chirpPatientDetails.microchip || undefined,
    manufacturedBy: chirpInventoryDetails.manufacturedBy || undefined,
    color: chirpPatientDetails.color || undefined,
    markingsTattoo: chirpPatientDetails.markingsTattoo || undefined,
    animalControlLicense: chirpPatientDetails.animalControlLicense || undefined,
    license: chirpPatientDetails.license || undefined,
    rabiesTag: chirpPatientDetails.rabiesTagNumber || undefined,
    serialNumber: chirpInventoryDetails.serialNumber || undefined,
    typeId: vaccineTypeId,
    vaccineTypeId,
  }
}
